/**
 * `<HomeDashboard/>` — the tasks-team overview Home (#1043, PR-D, founder
 * prod-inspection redirection F5, 2026-05-26).
 *
 * The HYBRID dashboard the founder chose: a per-outcome ROLLUP STRIP up top
 * (one editorial card per outcome root — progress bar + "N OF M DONE" + small
 * factual "X due soon" / "Y blocked" counts) over a TWO-COLUMN body — a
 * GitHub-issues-style, FILTERABLE task list on the LEFT (state glyph + title +
 * assignee avatars + due date + the outcome it belongs to) and a project-wide
 * HISTORY / activity feed on the RIGHT.
 *
 * Round-3 (founder prod-inspection item 2, 2026-05-27) moved History off the
 * Taskmap window (the docked rail was removed) and onto this dashboard, beside
 * the task list. The feed (`<ProjectActivityFeed/>`) shows the WHOLE project's
 * recent activity — it is NOT selection-driven here (the dashboard has no canvas
 * selection); it reads the project root's subtree, reusing the shipped timeline
 * primitives.
 *
 * It composes the editorial atoms (no dedicated wireframe screen): the lane /
 * rollup idiom from `V_B214` (serif-italic outcome title + a `t-issue`
 * "N OF M DONE" tag) and the list-row idiom from the `V_B43` notifications
 * inbox (rows with a state tag, an `Avatar`, body text, a right-aligned mono
 * time/date), recast as a task list.
 *
 * This component is PURE-PRESENTATIONAL — it takes its data (the built tree
 * forest, the member lookup + list, the team timezone, the loading flag, and a
 * pinned `now`) as props, so the rollup + list are fully testable without
 * mocking the network. The activity feed makes its OWN fetch via the shipped
 * `useNodeHistory` hook (kept self-contained, like the other history surfaces);
 * tests mock that hook. The data wrapper that wires `useTaskNodes` /
 * `useTaskTeamMembers` / `useTeam` lives in `MapHomePage.tsx`.
 *
 * Brand firewall (this is the most exposed surface):
 *   - Rollups count WORK (tasks done / due / blocked), NEVER people — no
 *     per-person completion counts, velocity, or ranking. Assignee avatars are
 *     a neutral "who's on it" (§A9). The history feed is provenance — a story of
 *     the work, never a per-person tally.
 *   - Blocked + due-soon/overdue render in calm AMBER, never alarm red.
 *   - No tier language; burnout never named; calm, factual copy throughout.
 *
 * Responsive: on a phone the two columns STACK (the task list first, then the
 * history feed); on desktop they sit side-by-side.
 *
 * Isolation (`taskmap ⊥ scheduler`): taskmap-local + the editorial theme + the
 * sanctioned shell `useIsMobile` only. No scheduler / cycle / solver imports.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useIsMobile } from "../../lib/useMediaQuery";
import { ProjectActivityFeed } from "../history/ProjectActivityFeed";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import { TeamZoneContext, useTeamZone } from "../theme/TeamZoneContext";
import type { TreeNode } from "../tree/buildTree";
import {
  type OutcomeStats,
  type TaskIssueRow,
  buildIssueRows,
  buildYourWorkRows,
  isOutcomeComplete,
  outcomeStats,
} from "../tree/outcomes";
import type { TaskState } from "../tree/state";
import { AvatarRow } from "./Avatar";
import { timingMeta } from "./taskTimingMeta";

/** The amber (never red) tone the firewall mandates for due/blocked emphasis. */
const AMBER = "#C9852D"; // --st-block (editorial blocked dot)

export interface HomeDashboardProps {
  /** The team's id — for links into the Taskmap board + the history feed fetch. */
  teamId: string | undefined;
  /** The built tree forest (one `TreeNode` per outcome root). */
  roots: readonly TreeNode[];
  /** `membershipId → TaskMember` for assignee avatars (the issues list). */
  membersById: Map<string, TaskMember>;
  /** The flat member roster — for actor/referenced names in the activity feed. */
  members: TaskMember[];
  /** Team IANA zone for the activity feed's day bucketing + timestamps. */
  teamTimezone?: string | undefined;
  /** True while the node list is loading (degrade to a calm skeleton). */
  isLoading: boolean;
  /**
   * The signed-in viewer's `membership_id` on this team (round-4 item ④,
   * #1043), used to derive the "Your work" slice — rows where the viewer is
   * an assignee AND the row is overdue / due-soon / blocked. Optional + may
   * be `null` (anonymous / not on this team) so the section degrades to its
   * calm empty state ("All caught up …") without coupling to auth.
   */
  currentMembershipId?: string | null;
  /**
   * How many unread @-mentions / note-subscription notifications the signed-in
   * viewer has on this team (round-4 item ④, #1043). When > 0 the "Your work"
   * section adds a calm chip linking to the Taskmap where the bell shows the
   * detail. Optional + defaults to 0, so a team with the notifications layer
   * off (or no awaiting mentions) just renders no chip.
   */
  mentionsUnread?: number;
  /**
   * The clock for the due-soon math. Injected (never `Date.now()` inline) so the
   * surface and tests pin the same boundary. Defaults to "now" for the wrapper.
   */
  now?: Date;
}

/** The "due" filter values. */
type DueFilter = "all" | "overdue" | "week" | "none";

const DUE_FILTERS: { key: DueFilter; label: string }[] = [
  { key: "all", label: "All dates" },
  { key: "overdue", label: "Overdue" },
  { key: "week", label: "This week" },
  { key: "none", label: "No date" },
];

const STATE_FILTERS: { key: TaskState | "all"; label: string }[] = [
  { key: "all", label: "All states" },
  { key: "blocked", label: "Blocked" },
  { key: "in_progress", label: "In progress" },
  { key: "planned", label: "Not started" },
  { key: "done", label: "Done" },
];

export function HomeDashboard({
  teamId,
  roots,
  membersById,
  members,
  teamTimezone,
  isLoading,
  currentMembershipId = null,
  mentionsUnread = 0,
  now = new Date(),
}: HomeDashboardProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const isMobile = useIsMobile();

  const boardHref = teamId ? `/teams/${teamId}/map/board` : "#";

  // Filters.
  const [outcomeFilter, setOutcomeFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<TaskState | "all">("all");
  const [dueFilter, setDueFilter] = useState<DueFilter>("all");

  // The per-outcome rollup numbers + the flat issues list, derived once.
  const outcomes = useMemo(
    () =>
      roots.map((root) => ({
        root,
        stats: outcomeStats(root, now),
      })),
    [roots, now],
  );
  const allRows = useMemo(() => buildIssueRows(roots, now), [roots, now]);
  // Round-4 item ④ (#1043) — the personal slice: rows the signed-in viewer is
  // assigned to AND that need attention now (overdue / due-soon / blocked).
  // Empty when no membership / no matching rows; the section degrades to its
  // calm "All caught up …" state.
  const yourWorkRows = useMemo(
    () => buildYourWorkRows(roots, currentMembershipId, now),
    [roots, currentMembershipId, now],
  );

  const rows = useMemo(
    () =>
      allRows.filter((row) => {
        if (outcomeFilter !== "all" && row.outcomeId !== outcomeFilter) return false;
        if (stateFilter !== "all" && row.node.state !== stateFilter) return false;
        if (dueFilter === "overdue" && !row.overdue) return false;
        if (dueFilter === "week" && !row.dueSoon) return false;
        if (dueFilter === "none" && row.node.targetDate) return false;
        return true;
      }),
    [allRows, outcomeFilter, stateFilter, dueFilter],
  );

  // Every outcome root for the activity feed — the feed fans out one
  // subtree-history fetch per id and merges newest-first (round-4 ⑤.c, QA-B
  // B1). Empty when there are no outcomes yet (the feed then shows its calm
  // empty state). A node id → title lookup over the whole forest names
  // referenced/descendant nodes in the timeline.
  const rootIds = useMemo(() => roots.map((r) => r.id), [roots]);
  const nodeTitleById = useMemo(() => {
    const m = new Map<string, string>();
    const walk = (node: TreeNode) => {
      m.set(node.id, node.title);
      for (const child of node.children) walk(child);
    };
    for (const root of roots) walk(root);
    return m;
  }, [roots]);

  return (
    <TeamZoneContext.Provider value={teamTimezone || undefined}>
      <div
        data-testid="map-home-dashboard"
        className="min-h-[calc(100vh-9rem)] w-full px-4 py-6 md:px-8"
        style={{ backgroundColor: c.canvas, color: c.ink, fontFamily: theme.fonts.sans }}
      >
        <header className="mb-6">
          <p
            className="mb-1 text-[11px] uppercase tracking-[0.18em]"
            style={{ color: c.muted, fontFamily: theme.fonts.mono }}
          >
            Tasks · Home
          </p>
          <h1 className="text-3xl" style={{ fontFamily: theme.fonts.serif, fontStyle: "italic" }}>
            The whole map at a glance.
          </h1>
        </header>

        {/* (A) Per-outcome rollup strip. */}
        <section data-testid="home-rollup-strip" aria-label="Outcomes" className="mb-8">
          {isLoading ? (
            <LoadingStrip theme={theme} />
          ) : outcomes.length === 0 ? null : (
            <div
              className={isMobile ? "flex flex-col gap-3" : "grid gap-3"}
              style={
                isMobile
                  ? undefined
                  : { gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }
              }
            >
              {outcomes.map(({ root, stats }) => (
                <OutcomeCard
                  key={root.id}
                  title={root.title}
                  stats={stats}
                  boardHref={boardHref}
                  theme={theme}
                />
              ))}
            </div>
          )}
        </section>

        {/* The two-column body: the LEFT column stacks "Your work" (round-4
          item ④, #1043) over the filterable "All tasks" list, and the
          project-wide activity feed sits on the RIGHT. On a phone the columns
          stack (your-work → tasks → feed). The feed column is a touch narrower
          than the list on desktop (the list is the focal column). */}
        <div
          data-testid="home-body"
          className={isMobile ? "flex flex-col gap-6" : "grid items-start gap-6"}
          style={isMobile ? undefined : { gridTemplateColumns: "minmax(0,1fr) 380px" }}
        >
          {/* LEFT column wrapper — holds the personal "Your work" slice over the
            generic filterable task list. `min-w-0` lets the grid item shrink so
            long titles don't push the row past the column's track. */}
          <div className="flex min-w-0 flex-col gap-6">
            {/* (B0) Your work — round-4 item ④, #1043. */}
            <YourWorkSection
              rows={yourWorkRows}
              mentionsUnread={mentionsUnread}
              boardHref={boardHref}
              membersById={membersById}
              isLoading={isLoading}
              theme={theme}
            />

            {/* (B) Filterable issues-style task list. */}
            <section data-testid="home-issues" aria-label="Tasks" className="min-w-0">
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <h2
                  className="text-lg"
                  style={{ fontFamily: theme.fonts.serif, fontStyle: "italic" }}
                >
                  Tasks
                </h2>
                {!isLoading && (
                  <span
                    data-testid="home-issues-count"
                    className="text-[11px] uppercase tracking-[0.08em]"
                    style={{ color: c.muted, fontFamily: theme.fonts.mono }}
                  >
                    {rows.length} of {allRows.length} shown
                  </span>
                )}
              </div>

              {/* Filter controls. */}
              <div data-testid="home-filters" className="mb-4 flex flex-wrap items-center gap-2">
                <FilterSelect
                  testId="home-filter-outcome"
                  label="Outcome"
                  value={outcomeFilter}
                  onChange={setOutcomeFilter}
                  options={[
                    { value: "all", label: "All outcomes" },
                    ...roots.map((r) => ({ value: r.id, label: r.title })),
                  ]}
                  theme={theme}
                />
                <FilterSelect
                  testId="home-filter-state"
                  label="State"
                  value={stateFilter}
                  onChange={(v) => setStateFilter(v as TaskState | "all")}
                  options={STATE_FILTERS.map((f) => ({ value: f.key, label: f.label }))}
                  theme={theme}
                />
                <FilterSelect
                  testId="home-filter-due"
                  label="Due"
                  value={dueFilter}
                  onChange={(v) => setDueFilter(v as DueFilter)}
                  options={DUE_FILTERS.map((f) => ({ value: f.key, label: f.label }))}
                  theme={theme}
                />
              </div>

              {isLoading ? (
                <LoadingRows theme={theme} />
              ) : allRows.length === 0 ? (
                <EmptyState boardHref={boardHref} theme={theme} />
              ) : rows.length === 0 ? (
                <div
                  data-testid="home-issues-no-match"
                  className="rounded-md border px-4 py-8 text-center text-sm"
                  style={{ borderColor: c.rule, color: c.muted, backgroundColor: c.cardBg }}
                >
                  No tasks match these filters.
                </div>
              ) : (
                <ul
                  data-testid="home-issues-list"
                  className="overflow-hidden rounded-md border"
                  style={{ borderColor: c.rule, backgroundColor: c.cardBg }}
                >
                  {rows.map((row) => (
                    <IssueRow key={row.node.id} row={row} membersById={membersById} theme={theme} />
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* (C) Project-wide activity feed (history relocated here round-3). It
            reads the project root's subtree via the shipped `useNodeHistory`
            hook + reuses `<TaskTimeline/>`; capped to a calm scroll height so
            the feed never dominates the page (it's a glance, not the focus).

            Containment (round-4 item ①, #1043): the bound is only useful if the
            feed is wired into a bounded flex chain — a `max-h` on this wrapper
            alone let the inner `<aside>` (content-height) overflow and grow the
            page unbounded. So on desktop this column is itself a `flex` column
            (`md:flex md:flex-col`) that is allowed to shrink to its cap
            (`md:min-h-0` overrides the grid item's automatic min-height) and is
            capped at the viewport (`md:max-h-…`); the feed then fills it
            (`md:flex-1`) and scrolls inside (its body is `overflow-auto`). On a
            phone the column stays block flow so the feed flows in the stacked
            page (no nested scroll). */}
          <section
            data-testid="home-activity"
            aria-label="Project activity"
            className="min-w-0 md:flex md:max-h-[calc(100vh-13rem)] md:min-h-0 md:flex-col"
          >
            <ProjectActivityFeed
              teamId={teamId}
              members={members}
              teamTimezone={teamTimezone}
              rootIds={rootIds}
              nodeTitleById={nodeTitleById}
            />
          </section>
        </div>
      </div>
    </TeamZoneContext.Provider>
  );
}

// ── (A) Outcome rollup card ────────────────────────────────────────────────

type Theme = ReturnType<typeof useMapTheme>;

function OutcomeCard({
  title,
  stats,
  boardHref,
  theme,
}: {
  title: string;
  stats: OutcomeStats;
  boardHref: string;
  theme: Theme;
}) {
  const c = theme.palette;
  const pct = stats.totalLeaves > 0 ? Math.round((stats.doneLeaves / stats.totalLeaves) * 100) : 0;
  // B8.4 (#1043) — a COMPLETE outcome (every leaf `done`) carries a calm
  // done-green "Completed" marker beside its 100% bar. Derived from the same
  // rollup; done-green (the sage dot), never red/amber — completion is a GOOD
  // state. `isOutcomeComplete` requires ≥1 leaf, so a blank outcome never marks.
  const complete = isOutcomeComplete(stats);
  const doneGreen = theme.stateStyle("done").dot ?? theme.stateStyle("done").fg;
  return (
    <Link
      to={boardHref}
      data-testid="home-outcome-card"
      data-complete={complete || undefined}
      className="block rounded-md border p-4 no-underline transition-colors hover:border-current"
      style={{ borderColor: c.rule, backgroundColor: c.cardBg, color: c.ink }}
    >
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <span
          className="text-[15px] leading-snug"
          style={{ fontFamily: theme.fonts.serif, fontStyle: "italic" }}
        >
          {title}
        </span>
        {complete ? (
          <span
            data-testid="home-outcome-completed"
            className="shrink-0 whitespace-nowrap text-[10px] uppercase tracking-[0.06em]"
            style={{ color: doneGreen, fontFamily: theme.fonts.mono }}
          >
            ✓ Completed
          </span>
        ) : (
          <span
            data-testid="home-outcome-rollup"
            className="shrink-0 whitespace-nowrap text-[10px] uppercase tracking-[0.06em]"
            style={{ color: c.rollupInk, fontFamily: theme.fonts.mono }}
          >
            {stats.doneLeaves} of {stats.totalLeaves} done
          </span>
        )}
      </div>

      {/* Progress bar (work done, never a person). Decorative — the
          "N of M done" text above is the accessible value; the bar carries an
          aria-label and is hidden from AT to avoid a redundant reading. */}
      <div
        data-testid="home-outcome-bar"
        className="mb-3 h-1.5 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: c.rollupTrack }}
        aria-hidden
        data-pct={pct}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: c.rollupFill }}
        />
      </div>

      {/* Small factual counts — amber when there's something to flag. */}
      <div className="flex items-center gap-4 text-[11px]" style={{ fontFamily: theme.fonts.mono }}>
        <span
          data-testid="home-outcome-duesoon"
          style={{ color: stats.dueSoonCount > 0 ? AMBER : c.muted }}
        >
          {stats.dueSoonCount} due soon
        </span>
        <span
          data-testid="home-outcome-blocked"
          style={{ color: stats.blockedCount > 0 ? AMBER : c.muted }}
        >
          {stats.blockedCount} blocked
        </span>
      </div>
    </Link>
  );
}

// ── (B0) Your work section (round-4 item ④, #1043) ─────────────────────────
// The personal slice: rows the signed-in viewer is assigned to AND that need
// attention now (overdue / due-soon / blocked). Plus a calm chip linking to
// the bell when there are unread mentions/notifications awaiting. About "your
// work", never a per-person scorecard / ranking (§A9). Renders even when the
// slice is empty so the user reads "I checked — you're all caught up."

function YourWorkSection({
  rows,
  mentionsUnread,
  boardHref,
  membersById,
  isLoading,
  theme,
}: {
  rows: readonly TaskIssueRow[];
  mentionsUnread: number;
  boardHref: string;
  membersById: Map<string, TaskMember>;
  isLoading: boolean;
  theme: Theme;
}) {
  const c = theme.palette;
  const hasRows = rows.length > 0;
  const hasMentions = mentionsUnread > 0;
  // While the tree is loading, this section stays out of the page — the
  // existing rollup + issues skeletons above carry the loading feel, and a
  // second `home-loading-rows` here would clash with the existing loading
  // test's "exactly one" assertion. The section mounts as soon as data lands.
  if (isLoading) return null;
  return (
    <section data-testid="home-your-work" aria-label="Your work" className="min-w-0">
      <YourWorkHeader theme={theme} count={hasRows ? rows.length : 0} />
      {hasMentions && (
        <Link
          to={boardHref}
          data-testid="home-your-work-mentions"
          className="mb-3 flex items-center gap-2 rounded-md border px-4 py-3 text-sm no-underline transition-colors hover:border-current"
          style={{ borderColor: c.rule, backgroundColor: c.cardBg, color: c.ink }}
        >
          <span aria-hidden style={{ color: AMBER }}>
            ✉
          </span>
          <span style={{ flex: 1 }}>
            {mentionsUnread} mention{mentionsUnread === 1 ? "" : "s"} awaiting you
          </span>
          <span
            className="text-[11px] uppercase tracking-[0.06em]"
            style={{ color: c.muted, fontFamily: theme.fonts.mono }}
          >
            open Taskmap →
          </span>
        </Link>
      )}
      {hasRows ? (
        <ul
          data-testid="home-your-work-list"
          className="overflow-hidden rounded-md border"
          style={{ borderColor: c.rule, backgroundColor: c.cardBg }}
        >
          {rows.map((row) => (
            <IssueRow key={row.node.id} row={row} membersById={membersById} theme={theme} />
          ))}
        </ul>
      ) : !hasMentions ? (
        <div
          data-testid="home-your-work-empty"
          className="rounded-md border px-4 py-6 text-center text-sm"
          style={{ borderColor: c.rule, color: c.muted, backgroundColor: c.cardBg }}
        >
          All caught up — nothing overdue, due-soon, or blocked is assigned to you.
        </div>
      ) : null}
    </section>
  );
}

function YourWorkHeader({ theme, count }: { theme: Theme; count: number | null }) {
  const c = theme.palette;
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
      <h2 className="text-lg" style={{ fontFamily: theme.fonts.serif, fontStyle: "italic" }}>
        Your work
      </h2>
      {count != null && (
        <span
          data-testid="home-your-work-count"
          className="text-[11px] uppercase tracking-[0.08em]"
          style={{ color: c.muted, fontFamily: theme.fonts.mono }}
        >
          {count === 0 ? "nothing for now" : `${count} item${count === 1 ? "" : "s"}`}
        </span>
      )}
    </div>
  );
}

// ── (B) Issue row ──────────────────────────────────────────────────────────

function IssueRow({
  row,
  membersById,
  theme,
}: {
  row: TaskIssueRow;
  membersById: Map<string, TaskMember>;
  theme: Theme;
}) {
  const c = theme.palette;
  const { node } = row;
  const style = theme.stateStyle(node.state);
  const assignees = node.assigneeIds
    .map((id) => membersById.get(id))
    .filter((m): m is TaskMember => m != null);
  const dueText = formatDueDate(node.targetDate);
  const teamZone = useTeamZone();
  const timingText = timingMeta(node, teamZone);
  const dueAmber = row.dueSoon; // overdue ⊆ dueSoon — both amber, never red.

  return (
    <li
      data-testid="home-issue-row"
      className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
      style={{ borderColor: c.rule }}
    >
      {/* State glyph + label. */}
      <span
        data-testid="home-issue-state"
        className="inline-flex shrink-0 items-center gap-1.5 text-xs"
        style={{ color: style.dot ?? style.fg }}
        title={style.label}
      >
        <span aria-hidden>{style.glyph}</span>
        <span className="hidden sm:inline" style={{ color: c.muted }}>
          {style.label}
        </span>
      </span>

      {/* Title. */}
      <span
        data-testid="home-issue-title"
        className="flex-1 truncate text-sm"
        style={{ color: c.ink }}
      >
        {node.title}
      </span>

      {/* Start time and place of a timed task (#2705). */}
      {timingText ? (
        <span
          data-testid="home-issue-timing"
          className="hidden max-w-[10rem] shrink-0 truncate text-[10px] font-semibold uppercase sm:inline-block"
          style={{ color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.06em" }}
        >
          {timingText}
        </span>
      ) : null}

      {/* Assignee avatars (a neutral who's-on-it). */}
      {assignees.length > 0 && (
        <span className="shrink-0">
          <AvatarRow members={assignees} size={20} />
        </span>
      )}

      {/* Outcome (root) label. */}
      <span
        data-testid="home-issue-outcome"
        className="hidden shrink-0 truncate text-[11px] md:inline-block md:max-w-[10rem]"
        style={{ color: c.muted, fontFamily: theme.fonts.mono }}
        title={row.outcomeTitle}
      >
        {row.outcomeTitle}
      </span>

      {/* Due date (mono; amber when due-soon/overdue; "—" when none). */}
      <span
        data-testid="home-issue-due"
        className="w-14 shrink-0 text-right text-[11px]"
        style={{ color: dueAmber ? AMBER : c.muted, fontFamily: theme.fonts.mono }}
      >
        {dueText}
      </span>
    </li>
  );
}

// ── Calm empty / loading / filter atoms ────────────────────────────────────

function EmptyState({ boardHref, theme }: { boardHref: string; theme: Theme }) {
  const c = theme.palette;
  return (
    <div
      data-testid="home-empty"
      className="rounded-md border px-6 py-12 text-center"
      style={{ borderColor: c.rule, backgroundColor: c.cardBg }}
    >
      <p className="mb-4 text-sm" style={{ color: c.muted }}>
        No tasks yet — open the Taskmap to start.
      </p>
      <Link
        to={boardHref}
        data-testid="home-empty-open-board"
        className="inline-block rounded-md px-4 py-2 text-sm font-semibold text-white no-underline transition-opacity hover:opacity-90"
        style={{ backgroundColor: c.teal }}
      >
        Open the Taskmap →
      </Link>
    </div>
  );
}

function LoadingStrip({ theme }: { theme: Theme }) {
  const c = theme.palette;
  return (
    <div
      data-testid="home-loading-strip"
      className="grid gap-3"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
    >
      {[0, 1].map((i) => (
        <div
          key={i}
          className="h-24 animate-pulse rounded-md border"
          style={{ borderColor: c.rule, backgroundColor: c.bgRecess }}
        />
      ))}
    </div>
  );
}

function LoadingRows({ theme }: { theme: Theme }) {
  const c = theme.palette;
  return (
    <div
      data-testid="home-loading-rows"
      className="overflow-hidden rounded-md border"
      style={{ borderColor: c.rule, backgroundColor: c.cardBg }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-12 animate-pulse border-b last:border-b-0"
          style={{ borderColor: c.rule, backgroundColor: c.bgRecess }}
        />
      ))}
    </div>
  );
}

function FilterSelect({
  testId,
  label,
  value,
  onChange,
  options,
  theme,
}: {
  testId: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  theme: Theme;
}) {
  const c = theme.palette;
  return (
    <label className="flex items-center gap-1.5 text-[11px]" style={{ color: c.muted }}>
      <span className="uppercase tracking-[0.08em]" style={{ fontFamily: theme.fonts.mono }}>
        {label}
      </span>
      <select
        data-testid={testId}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border px-2 py-1 text-xs"
        style={{
          borderColor: c.rule,
          backgroundColor: c.cardBg,
          color: c.ink,
          fontFamily: theme.fonts.sans,
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Format a date-only `YYYY-MM-DD` target date as a short "MMM D" mono string,
 * parsing the parts MANUALLY so it never timezone-shifts (`new Date("2026-…")`
 * would parse as UTC midnight and roll back a day in negative-offset zones).
 * Returns "—" for no date / a malformed value.
 */
export function formatDueDate(iso: string | null): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return "—";
  const month = Number(m[2]);
  const day = Number(m[3]);
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const name = MONTHS[month - 1];
  if (!name) return "—";
  return `${name} ${day}`;
}
